/**
 * 🍪 CookieConsentBanner — non-intrusive, GDPR/CCPA-friendly banner.
 *
 * Behavior:
 *   • Renders once, anchored bottom-left, only when consent is unset.
 *   • "Accept" → localStorage.life_cookie_consent = "accepted"  → enables GA4 loader.
 *   • "Decline" → localStorage.life_cookie_consent = "declined" → GA4 stays silent.
 *   • "Manage" (link) routes to /life-empowerment/privacy for the full policy.
 *   • Reloads the page on accept so the GA4 useEffect re-runs cleanly.
 */
import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";

const KEY = "life_cookie_consent";

export default function CookieConsentBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(KEY);
      if (!v) setOpen(true);
    } catch { /* private mode → just show it */ setOpen(true); }
  }, []);

  if (!open) return null;

  const decide = (verdict) => {
    try { localStorage.setItem(KEY, verdict); } catch { /* ignore */ }
    setOpen(false);
    if (verdict === "accepted") {
      // Re-trigger GA4 hook on next paint.
      window.location.reload();
    }
  };

  return (
    <div
      data-testid="life-cookie-banner"
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-3 left-3 right-3 md:right-auto md:max-w-md z-50 bg-white border border-[#dee2e6] shadow-[0_12px_42px_rgba(31,58,96,0.18)] p-4 rounded-sm"
    >
      <div className="flex items-start gap-3">
        <Cookie className="h-5 w-5 text-[#d4af37] shrink-0 mt-0.5"/>
        <div className="flex-1 min-w-0 text-[13px] leading-relaxed text-[#212529]">
          <div className="font-display font-semibold text-[#1f3a60] mb-1">
            We respect your privacy.
          </div>
          <p>
            We use only essential cookies plus, with your consent, anonymized
            analytics to make the site better. See our{" "}
            <Link to="/life-empowerment/privacy" className="text-[#b7092b] hover:underline font-semibold">
              Privacy Policy
            </Link>{" "}
            for details. You can change this any time.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button
              onClick={() => decide("accepted")}
              data-testid="life-cookie-accept"
              className="life-cta-gold px-4 py-2 font-mono text-[10px] tracking-[0.22em] uppercase"
            >
              Accept
            </button>
            <button
              onClick={() => decide("declined")}
              data-testid="life-cookie-decline"
              className="life-cta-ghost px-4 py-2 font-mono text-[10px] tracking-[0.22em] uppercase"
            >
              Decline
            </button>
          </div>
        </div>
        <button
          onClick={() => decide("declined")}
          aria-label="Dismiss"
          className="text-[#1f3a60]/45 hover:text-[#b7092b]"
        >
          <X className="h-4 w-4"/>
        </button>
      </div>
    </div>
  );
}
